import { ACCOUNT_BANNED_ERROR } from '@app/common';
import type { ActiveBan, AppResponse } from '@app/common';

import { env } from '../config/env';
import { supabase } from '../lib/supabase';
import { notifySessionExpired } from '../lib/sessionExpiry';
import { notifyAccountSuspended } from '../lib/accountSuspended';

/**
 * Thrown when the backend rejects the request's token, so call sites can tell a
 * dead session apart from a genuine API failure instead of matching on the message.
 */
export class SessionExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/**
 * Thrown when the account itself is suspended. Separate from `SessionExpiredError`
 * because signing in again would not fix it, and call sites suppress it for the
 * same reason they suppress that one — the dialog is already covering the screen.
 */
export class AccountSuspendedError extends Error {
  constructor(readonly ban: ActiveBan) {
    super('Account suspended');
    this.name = 'AccountSuspendedError';
  }
}

/**
 * Picks the ban out of a 403 body, or returns null if this is some other 403.
 *
 * Two shapes are accepted because two code paths produce them: `extractAuthUser`
 * writes its refusal at the top level, while anything going through `sendFailure`
 * nests it under `errors`. Matching on the code rather than the status keeps the
 * plain `FORBIDDEN` a non-admin gets from being mistaken for a suspension.
 */
function readBan(json: AppResponse<unknown>): ActiveBan | null {
  const body = json as unknown as Record<string, unknown>;
  const nested = body.errors;
  const source = (
    nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : body
  ) as Record<string, unknown>;

  if (source.error !== ACCOUNT_BANNED_ERROR) return null;

  return {
    reason: typeof source.reason === 'string' ? source.reason : null,
    bannedUntil: typeof source.bannedUntil === 'string' ? source.bannedUntil : null,
  };
}

// Backend sendFailure wraps errors as: { success: false, errors: { message, error, ... } }
// This helper extracts the best human-readable message from any error shape.
function extractErrorMessage(json: AppResponse<unknown>, httpStatus: number): string {
  const errors = (json as unknown as Record<string, unknown>).errors;
  if (errors && typeof errors === 'object') {
    const e = errors as Record<string, unknown>;
    if (typeof e.message === 'string' && e.message) return e.message;
    if (typeof e.error === 'string' && e.error) return e.error;
  }
  if (typeof json.message === 'string' && json.message) return json.message;
  if (typeof json.error === 'string' && json.error) return json.error;
  return `Request failed with status ${httpStatus}`;
}

export const apiFetch = async <T>(path: string, options?: RequestInit): Promise<T> => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...options,
  });

  const json: AppResponse<T> = await response.json();

  // `extractAuthUser` answers 401 for both a missing header and an expired token, and
  // either way the user has to sign in again. Tearing the session down here — the one
  // place every request passes through — keeps each call site from having to guess.
  if (response.status === 401) {
    notifySessionExpired();
    throw new SessionExpiredError(extractErrorMessage(json, response.status));
  }

  // A suspension is answered 403 precisely so it does NOT land in the branch above:
  // the session is still valid, the account is not, and telling the user to sign in
  // again would send them at a login Supabase is already refusing.
  if (response.status === 403) {
    const ban = readBan(json);
    if (ban) {
      notifyAccountSuspended(ban);
      throw new AccountSuspendedError(ban);
    }
  }

  if (!response.ok || !json.success) {
    throw new Error(extractErrorMessage(json, response.status));
  }

  return json.data as T;
};
